import { AuditLog } from '@oxappsec/ox-unified-audit-logger';
import { Nullable } from 'common-types';
import dayjs from 'dayjs';
import { snapshot } from 'valtio';
import {
  FilterElements,
  FilterTypeToFilter,
} from '../../common/filters-utils/filters-utils';
import AuditLogsStore from '../stores/audit-logs-store';
import {
  AuditLogFilters,
  DateRange,
  GetLogsInput,
} from '../types/audit-logs-types';
import { getOneDayAgo, getTimeSec } from '../utils/date-utils';

export const addAuditLogs = (auditLogsBatch: AuditLog[]) => {
  const { auditLogs, offset } = snapshot(AuditLogsStore);

  AuditLogsStore.auditLogs = [...auditLogs, ...auditLogsBatch];
  AuditLogsStore.offset = offset + auditLogsBatch.length;
};

export const setTotal = (total?: number) => {
  if (total === undefined) {
    return;
  }

  AuditLogsStore.total = total;
};

export const clearAuditLogs = () => {
  AuditLogsStore.auditLogs = [];
  AuditLogsStore.total = 0;
  AuditLogsStore.offset = 0;
};

export const getAuditLogsQueryInput = () => {
  const { offset, orderBy, datePickerFrom, datePickerTo, filterBy } =
    snapshot(AuditLogsStore);

  const dateRange = getDateRange(datePickerFrom, datePickerTo);

  const input: GetLogsInput = {
    dateRange,
    skip: offset,
    limit: 50,
    orderBy,
    logTypes: filterBy[FilterTypeToFilter.logTypes],
    logNames: filterBy[FilterTypeToFilter.logNames],
    userEmails: filterBy[FilterTypeToFilter.userEmails],
  };

  return input;
};

export const setSelectedLog = (auditLog: AuditLog) => {
  AuditLogsStore.selectedAuditLogId = auditLog.id;
  AuditLogsStore.selectedAuditLog = auditLog;
};

export const clearSelectedLog = () => {
  AuditLogsStore.selectedAuditLogId = null;
  AuditLogsStore.selectedAuditLog = null;
};

export const setLastDrawerHeight = (height: number) => {
  AuditLogsStore.lastDrawerHeight = height;
};

export const getDateRange = (
  dateFrom: Nullable<dayjs.Dayjs>,
  dateTo: Nullable<dayjs.Dayjs>,
) => {
  const dateFromSec = getTimeSec(dateFrom);
  const dateToSec = getTimeSec(dateTo);

  return {
    from: dateFromSec ? dateFromSec : getOneDayAgo(),
    to: dateToSec ? dateToSec : new Date(),
  } as DateRange;
};

export const toggleFiltersPanel = () => {
  AuditLogsStore.filtersOpen = !AuditLogsStore.filtersOpen;
};

export const setAuditLogFilters = (filters: AuditLogFilters) => {
  AuditLogsStore.filters = filters;
};

export const setInitialDatePicker = () => {
  AuditLogsStore.datePickerFrom = dayjs(getOneDayAgo());
  AuditLogsStore.datePickerTo = dayjs(new Date());
};

export const clearDatePicker = () => {
  AuditLogsStore.datePickerFrom = null;
  AuditLogsStore.datePickerTo = null;
};

export const setDatePickerFrom = (date: Nullable<dayjs.Dayjs>) => {
  AuditLogsStore.datePickerFrom = date;
};

export const setDatePickerTo = (date: Nullable<dayjs.Dayjs>) => {
  AuditLogsStore.datePickerTo = date;
};

export const setSearchValues = (type: string, searchValue: string) => {
  AuditLogsStore.searchValues[type] = searchValue;
};

export const setStoreAuditLogFiltersTypeSearch = (
  auditLogFilters: AuditLogFilters,
): void => {
  const { searchValues } = snapshot(AuditLogsStore);
  AuditLogsStore.filtersTypeSearch = Object.keys(auditLogFilters).reduce(
    (acc, key) => {
      acc[key] = searchValues[key]
        ? auditLogFilters[key].filter((f: FilterElements) =>
            f.label
              .toLocaleLowerCase()
              .includes(searchValues[key].toLowerCase()),
          )
        : auditLogFilters[key];
      return acc;
    },
    {} as AuditLogFilters,
  );
};
